<template>
  <div>
    <RevDrawer
      name="modal.listings.tab.filters.mobile"
      :title="i18n(sharedTranslations.title)"
    >
      <template #body>
        <form>
          <div class="flex flex-col space-y-12">
            <RevInputText
              v-if="shouldShowFilter(FILTER_FIELDS.TITLE)"
              :id="FILTER_FIELDS.TITLE"
              v-model="filtersValues.title"
              :label="i18n(sharedTranslations.title)"
              type="text"
            />
            <RevInputText
              v-if="shouldShowFilter(FILTER_FIELDS.SKU)"
              :id="FILTER_FIELDS.SKU"
              v-model="filtersValues.sku"
              :label="i18n(sharedTranslations.sku)"
              type="text"
            />
            <RevInputSelect
              v-if="shouldShowFilter(FILTER_FIELDS.BACKBOX)"
              :id="FILTER_FIELDS.BACKBOX"
              v-model="filtersValues.hasBackbox"
              :label="i18n(sharedTranslations.backbox)"
              :options="backboxOptions"
            />
            <RevInputSelect
              v-if="shouldShowFilter(FILTER_FIELDS.STOCK)"
              :id="FILTER_FIELDS.STOCK"
              v-model="filtersValues.hasStock"
              :label="i18n(sharedTranslations.stock)"
              :options="stockOptions"
            />
            <RevInputText
              v-if="shouldShowFilter(FILTER_FIELDS.UUID)"
              :id="FILTER_FIELDS.UUID"
              v-model="filtersValues.productId"
              :label="i18n(sharedTranslations.uuid)"
              type="text"
            />

            <RevInputSelect
              v-if="shouldShowFilter(FILTER_FIELDS.GRADE)"
              :id="FILTER_FIELDS.GRADE"
              v-model="filtersValues.aestheticGrade"
              :label="i18n(sharedTranslations.grade)"
              :options="gradesOptions"
            />

            <RevInputMultiSelect
              v-if="shouldShowFilter(FILTER_FIELDS.MARKET)"
              :id="FILTER_FIELDS.MARKET"
              v-model="marketSelectValue"
              :custom-display-value="setCustomMarketDisplayValue"
              :label="i18n(sharedTranslations.markets)"
              :options="availableMarketsOptions"
            />
            <SelectCategory
              v-if="shouldShowFilter(FILTER_FIELDS.CATEGORY_ID)"
              :id="FILTER_FIELDS.CATEGORY_ID"
              v-model="filtersValues.categoryId"
            />
            <RevInputSelect
              v-if="shouldShowFilter(FILTER_FIELDS.HAS_NEW_BATTERY)"
              :id="FILTER_FIELDS.HAS_NEW_BATTERY"
              v-model="filtersValues.hasNewBattery"
              :label="i18n(sharedTranslations.hasNewBattery)"
              :options="hasNewBatteryOptions"
            />
            <BackBoxRangeInput
              :id="FILTER_FIELDS.BACKBOX_PRICE_RANGE"
              v-model:backboxPriceCurrencyCode="
                filtersValues.backboxPriceCurrencyCode
              "
              v-model:backboxPriceMaxRange="filtersValues.backboxPriceMaxRange"
              v-model:backboxPriceMinRange="filtersValues.backboxPriceMinRange"
              v-model:backboxPriceRangeType="
                filtersValues.backboxPriceRangeType
              "
              v-model:withOrWithoutBackbox="filtersValues.hasBackbox"
              :currencyOptions="backMarketRangeCurrencyOptions"
            />
            <RevInputSelect
              v-if="shouldShowFilter(FILTER_FIELDS.PRICING_STRATEGY)"
              :id="FILTER_FIELDS.PRICING_STRATEGY"
              v-model="filtersValues.pricingStrategy"
              :label="i18n(sharedTranslations.pricingStrategy)"
              :options="pricingStrategyOptions"
            />
          </div>
        </form>
      </template>
      <template #footer="{ close }">
        <div class="flex">
          <RevButton
            class="mr-6"
            data-qa="submit-listings-filters"
            full-width="always"
            type="submit"
            variant="primary"
            @click="handleSubmit(close)"
          >
            {{ i18n(sharedTranslations.submit) }}
          </RevButton>

          <RevButton
            full-width="always"
            variant="secondary"
            @click="handleReset(close)"
          >
            {{ i18n(sharedTranslations.resetFilters) }}
          </RevButton>
        </div>
      </template>
      <template #trigger="{ open }">
        <RevStickyBar>
          <div class="flex items-center justify-center">
            <RevButton
              :icon="IconEqualizer"
              size="small"
              variant="secondary"
              @click="open"
            >
              {{ i18n(sharedTranslations.filter) }}
            </RevButton>
          </div>
        </RevStickyBar>
      </template>
    </RevDrawer>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import type { MarketCountryCode } from '@backmarket/http-api/src/api-models/MarketCountryCode'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevButton } from '@ds/components/Button'
import { RevDrawer } from '@ds/components/Drawer'
import { RevInputMultiSelect } from '@ds/components/InputMultiSelect'
import type { MultiSelectOption } from '@ds/components/InputMultiSelect/InputMultiSelect.type'
import { RevInputSelect } from '@ds/components/InputSelect'
import { RevInputText } from '@ds/components/InputText'
import { RevStickyBar } from '@ds/components/StickyBar'
import { IconEqualizer } from '@ds/icons/IconEqualizer'

import SelectCategory from '../../../../../../../../components/Input/SelectCategory/SelectCategory.vue'
import { useFiltersOptions } from '../../../../composables/useFiltersOptions'
import { FILTER_FIELDS } from '../../Filters.constants'
import sharedTranslations from '../../Filters.translations'
import type { InitialFilterValues } from '../../Filters.types'
import BackBoxRangeInput from '../BackBoxRangeInput/BackBoxRangeInput.vue'

const filtersValues = defineModel<InitialFilterValues>('filtersValues', {
  required: true,
})

const props = defineProps<{
  resetFilters: () => void
  submitFilters: () => void
  shouldShowFilter: (filter: string) => boolean
}>()

const i18n = useI18n()

const tracking = useTracking()

const {
  availableMarketsOptions,
  backboxOptions,
  gradesOptions,
  hasNewBatteryOptions,
  stockOptions,
  backMarketRangeCurrencyOptions,
  pricingStrategyOptions,
} = useFiltersOptions()

const marketSelectValue = computed({
  get: () => {
    if (filtersValues.value.markets === '') {
      return ['']
    }

    if (Array.isArray(filtersValues.value.markets)) {
      return filtersValues.value.markets as MarketCountryCode[]
    }

    return [filtersValues.value.markets] as MarketCountryCode[]
  },
  set: (value: Array<MarketCountryCode | ''>) => {
    // If 'All' is selected, we simply set an empty string
    if (value.includes('') && value.length === 0) {
      filtersValues.value.markets = ''
    }
    // If 'All' is selected and there are already other selected markets, we clear the other markets
    if (
      value.includes('') &&
      value.length >= 1 &&
      value.indexOf('') === value.length - 1
    ) {
      filtersValues.value.markets = ''
    }
    // When we set the other markets, we clear the 'All'
    else {
      filtersValues.value.markets = value.filter(
        (item) => item !== '',
      ) as unknown as string
    }
  },
})

const setCustomMarketDisplayValue = (
  selectedOptions: MultiSelectOption[] | undefined,
) => {
  if (!selectedOptions) {
    return undefined
  }
  if (selectedOptions.length <= 1) {
    return undefined
  }

  // If there is more than 1 market selected, we display the number of markets selected
  return i18n(sharedTranslations.marketsSelected, {
    numberOfMarkets: selectedOptions.length,
  })
}

function handleSubmit(closeModal: () => void) {
  tracking.trackClick({
    page_type: 'listings',
    zone: 'listings_filters',
    name: 'back_box_price_range_filter_is_applied',
    value: filtersValues.value.backboxPriceRangeType ? 1 : 0,
  })

  props.submitFilters()
  closeModal()
}

function handleReset(closeModal: () => void) {
  props.resetFilters()
  closeModal()
}
</script>
